// Distributor KG targets — enter per distributor (monthly or quarterly, up to 3
// levels), bulk upload/export, and a by-user roll-up. User targets are not typed:
// each user's target is the per-level sum of their mapped distributors.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Save, Search, Target } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import PageHeader from "@/components/PageHeader";
import BulkActionsBar from "@/components/bulk/BulkActionsBar";
import UserShares from "@/components/targets/UserShares";
import PeriodPicker, { currentMonthStart } from "@/components/targets/PeriodPicker";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const kg = (v) => (v == null || v === "" ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 1 }));

function Pcts({ progress }) {
  if (!progress?.length) return <span className="text-neutral-300">—</span>;
  return (
    <div className="flex gap-1.5 flex-wrap">
      {progress.map((p) => (
        <span key={p.level} title={`${p.gap_kg} kg to go`}
              className={"text-[11px] px-1.5 py-0.5 rounded font-semibold " + (p.reached ? "bg-emerald-100 text-emerald-700" : "bg-neutral-100 text-neutral-600")}>
          T{p.level} {p.pct}%
        </span>
      ))}
    </div>
  );
}

export default function DistributorTargets() {
  const [tab, setTab] = useState("distributors");
  const [period, setPeriod] = useState({ periodType: "month", periodStart: currentMonthStart() });
  const [q, setQ] = useState("");
  const [zoneId, setZoneId] = useState("all");
  const [zones, setZones] = useState([]);
  const [onlyWithTarget, setOnlyWithTarget] = useState(false);
  const [role, setRole] = useState("all");
  const [page, setPage] = useState(1);
  const PAGE = 50;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});          // distributor_id -> {t1,t2,t3} strings
  const [saving, setSaving] = useState(null);
  const [byUser, setByUser] = useState(null);
  const [incEdits, setIncEdits] = useState({});    // user_id -> {i1,i2,i3} strings

  useEffect(() => { api.get("/zones").then((r) => setZones(r.data.items || [])).catch(() => {}); }, []);
  useEffect(() => { setPage(1); }, [period, q, zoneId, onlyWithTarget, role, tab]);

  const baseParams = useMemo(() => ({
    period_type: period.periodType, period_start: period.periodStart,
    ...(zoneId !== "all" ? { zone_id: zoneId } : {}), ...(q.trim() ? { q: q.trim() } : {}),
  }), [period, zoneId, q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "shares") { setLoading(false); return; }
      if (tab === "distributors") {
        const r = await api.get("/distributor-targets", { params: { ...baseParams, with_target_only: onlyWithTarget, page, page_size: PAGE } });
        setData(r.data); setEdits({});
      } else {
        const r = await api.get("/distributor-targets/by-user", { params: { ...baseParams, ...(role !== "all" ? { role_code: role } : {}), page, page_size: PAGE } });
        setByUser(r.data); setIncEdits({});
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [tab, baseParams, onlyWithTarget, role, page]);
  useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [load, q]);

  const cur = (row) => edits[row.distributor_id] ?? { t1: row.t1_kg ?? "", t2: row.t2_kg ?? "", t3: row.t3_kg ?? "" };
  const setEdit = (row, k, v) => setEdits((e) => ({ ...e, [row.distributor_id]: { ...cur(row), [k]: v } }));
  const dirty = (row) => {
    const e = edits[row.distributor_id]; if (!e) return false;
    const n = (v) => (v === "" || v == null ? null : Number(v));
    return n(e.t1) !== row.t1_kg || n(e.t2) !== row.t2_kg || n(e.t3) !== row.t3_kg;
  };
  const dirtyRows = (data?.items || []).filter(dirty);

  // ---- incentive amounts (By user tab)
  const nn = (v) => (v === "" || v == null ? null : Number(v));
  const incCur = (u) => incEdits[u.user_id] ?? { i1: u.i1_amount ?? "", i2: u.i2_amount ?? "", i3: u.i3_amount ?? "" };
  const setInc = (u, k, v) => setIncEdits((e) => ({ ...e, [u.user_id]: { ...incCur(u), [k]: v } }));
  const incDirty = (u) => {
    const e = incEdits[u.user_id]; if (!e) return false;
    return nn(e.i1) !== u.i1_amount || nn(e.i2) !== u.i2_amount || nn(e.i3) !== u.i3_amount;
  };
  const saveInc = async (u) => {
    const e = incCur(u);
    setSaving(u.user_id);
    try {
      await api.put("/distributor-targets/incentive", { user_id: u.user_id, period_type: period.periodType,
        period_start: period.periodStart, i1_amount: nn(e.i1), i2_amount: nn(e.i2), i3_amount: nn(e.i3) });
      toast.success(`${u.name}: incentive saved`); load();
    } catch (err) { toast.error(`${u.name}: ${formatApiError(err)}`); }
    finally { setSaving(null); }
  };
  const downloadReport = async (format) => {
    try {
      const r = await api.get("/reports/target-incentive", { responseType: "blob", params: {
        period_type: period.periodType, period_start: period.periodStart, format,
        ...(zoneId !== "all" ? { zone_id: zoneId } : {}), ...(role !== "all" ? { role_code: role } : {}) } });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data); a.download = `target-incentive-${period.periodStart}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (err) { toast.error("Could not download the report. Please try again."); }
  };

  const saveRow = async (row) => {
    const e = cur(row); const n = (v) => (v === "" || v == null ? null : Number(v));
    setSaving(row.distributor_id);
    try {
      if (n(e.t1) == null && row.target_id) {
        await api.delete(`/distributor-targets/${row.target_id}`);
      } else {
        await api.put("/distributor-targets", { distributor_id: row.distributor_id, period_type: period.periodType,
          period_start: period.periodStart, t1_kg: n(e.t1), t2_kg: n(e.t2), t3_kg: n(e.t3) });
      }
      return true;
    } catch (err) { toast.error(`${row.code}: ${formatApiError(err)}`); return false; }
    finally { setSaving(null); }
  };
  const saveOne = async (row) => { if (await saveRow(row)) { toast.success(`${row.code} saved`); load(); } };
  const saveAll = async () => {
    let ok = 0;
    for (const r of dirtyRows) { if (await saveRow(r)) ok += 1; }
    if (ok) toast.success(`${ok} target${ok === 1 ? "" : "s"} saved`);
    load();
  };

  const T = data?.totals;
  return (
    <div className="p-4 space-y-4" data-testid="distributor-targets-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Money" }, { label: "Distributor Targets" }]}
        title="Distributor Targets (kg)"
        subtitle="Set each distributor's target for a month or quarter — up to 3 levels. A user's target is the sum of the distributors mapped to them; achievement is the kg of all secondary orders on those distributors."
        actions={
          tab === "shares" ? (
            <BulkActionsBar masterKey="user_distributor_share" label="User shares" onImportDone={() => {}} />
          ) : tab === "users" ? (
            <div className="flex items-center gap-2 flex-wrap">
              <BulkActionsBar masterKey="user_incentives" label="User incentives"
                buildExportFilters={() => ({ period_type: period.periodType, period_start: period.periodStart })}
                onImportDone={() => load()} />
              <Button variant="outline" size="sm" onClick={() => downloadReport("xlsx")} data-testid="dt-inc-report-xlsx">Incentive report (Excel)</Button>
              <Button variant="outline" size="sm" onClick={() => downloadReport("pdf")} data-testid="dt-inc-report-pdf">PDF</Button>
            </div>
          ) : (
            <BulkActionsBar masterKey="distributor_targets" label="Distributor targets"
              buildExportFilters={() => ({ period_type: period.periodType, period_start: period.periodStart })}
              onImportDone={() => load()} />
          )
        }
      />

      <div className="flex flex-wrap items-end gap-3">
        <PeriodPicker periodType={period.periodType} periodStart={period.periodStart} onChange={setPeriod} testid="dt-period" />
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-neutral-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} autoFocus
                 placeholder={tab === "users" ? "Search user name / phone…" : "Search distributor by name or code…"}
                 className="pl-8 pr-8 h-9 w-full" data-testid="dt-search" />
          {q && (
            <button type="button" onClick={() => setQ("")} aria-label="Clear search"
                    className="absolute right-2 top-1.5 text-neutral-400 hover:text-neutral-700 text-lg leading-none">×</button>
          )}
        </div>
        <Select value={zoneId} onValueChange={setZoneId}>
          <SelectTrigger className="w-[130px] h-9" data-testid="dt-zone"><SelectValue placeholder="Zone" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All zones</SelectItem>
            {zones.map((z) => <SelectItem key={z.id} value={z.id}>{z.code}</SelectItem>)}
          </SelectContent>
        </Select>
        {tab === "distributors" ? (
          <label className="flex items-center gap-1.5 text-xs text-neutral-600 h-9">
            <input type="checkbox" checked={onlyWithTarget} onChange={(e) => setOnlyWithTarget(e.target.checked)} data-testid="dt-only-with-target" />
            Only distributors with a target
          </label>
        ) : (
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="w-[130px] h-9" data-testid="dt-role"><SelectValue placeholder="Role" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {["SE", "ASM", "ZSM", "RSM", "SALES_HEAD"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="flex gap-2">
        {[["distributors", "Distributor targets"], ["users", "By user (derived)"], ["shares", "User shares (%)"]].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`dt-tab-${k}`}
                  className={"rounded-full border px-3 py-1 text-xs font-medium " + (tab === k ? "bg-[#6D2C91] border-[#6D2C91] text-white" : "bg-white border-neutral-200 text-neutral-700")}>
            {label}
          </button>
        ))}
      </div>

      {tab === "distributors" && T && (
        <div className="flex flex-wrap gap-2 text-xs" data-testid="dt-totals">
          {[["Target 1", T.t1], ["Target 2", T.t2], ["Target 3", T.t3]].map(([l, v]) => (
            <div key={l} className="rounded-lg border border-neutral-200 bg-white px-3 py-1.5"><span className="text-neutral-500">{l} total </span><b>{kg(v)} kg</b></div>
          ))}
          <div className="rounded-lg border border-neutral-200 bg-white px-3 py-1.5"><span className="text-neutral-500">Achieved </span><b>{kg(T.achieved_kg)} kg</b></div>
          <div className="rounded-lg border border-neutral-200 bg-white px-3 py-1.5"><span className="text-neutral-500">Distributors with target </span><b>{T.distributors_with_target}</b></div>
        </div>
      )}

      {tab === "shares" && <UserShares periodType={period.periodType} periodStart={period.periodStart} />}

      {tab !== "shares" && <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-0 overflow-x-auto">
          {loading && !(data || byUser) ? <div className="py-16 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-neutral-400" /></div> : tab === "distributors" ? (
            <table className="w-full text-sm" data-testid="dt-table">
              <thead className="bg-neutral-50 text-[11px] uppercase text-neutral-500 tracking-widest">
                <tr>
                  <th className="text-left py-2.5 px-3">Distributor</th><th className="text-left px-2">Zone</th>
                  <th className="text-left px-2">Target 1 (kg)</th><th className="text-left px-2">Target 2 (kg)</th><th className="text-left px-2">Target 3 (kg)</th>
                  <th className="text-right px-2">Achieved (kg)</th><th className="text-left px-2">% of target</th><th className="px-2" />
                </tr>
              </thead>
              <tbody className={loading ? "opacity-60" : ""}>
                {(data?.items || []).map((r) => {
                  const e = cur(r);
                  return (
                    <tr key={r.distributor_id} className="border-t border-neutral-100" data-testid={`dt-row-${r.code}`}>
                      <td className="py-1.5 px-3"><div className="font-medium">{r.name}</div><div className="text-[11px] text-neutral-400 font-mono">{r.code}</div></td>
                      <td className="px-2 text-neutral-500">{r.zone}{r.subzone !== "—" ? ` · ${r.subzone}` : ""}</td>
                      {["t1", "t2", "t3"].map((k) => (
                        <td key={k} className="px-2">
                          <Input type="number" min="0" step="0.1" value={e[k]} onChange={(ev) => setEdit(r, k, ev.target.value)}
                                 className="h-8 w-[110px]" data-testid={`dt-${k}-${r.code}`} />
                        </td>
                      ))}
                      <td className="px-2 text-right font-mono">{kg(r.achieved_kg)}</td>
                      <td className="px-2"><Pcts progress={r.progress} /></td>
                      <td className="px-2">
                        {dirty(r) && (
                          <Button size="sm" className="h-8" disabled={saving === r.distributor_id} onClick={() => saveOne(r)} data-testid={`dt-save-${r.code}`}>
                            {saving === r.distributor_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />} Save
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!loading && (data?.items || []).length === 0 && <tr><td colSpan={8} className="py-10 text-center text-neutral-400">No distributors match.</td></tr>}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm" data-testid="dt-user-table">
              <thead className="bg-neutral-50 text-[11px] uppercase text-neutral-500 tracking-widest">
                <tr>
                  <th className="text-left py-2.5 px-3">User</th><th className="text-left px-2">Role</th><th className="text-left px-2">Zone</th>
                  <th className="text-left px-2">Based on</th>
                  <th className="text-right px-2">Target 1</th><th className="text-right px-2">Target 2</th><th className="text-right px-2">Target 3</th>
                  <th className="text-right px-2">Secondary (kg)</th><th className="text-right px-2">Primary (kg)</th><th className="text-left px-2">% of target</th>
                  <th className="text-left px-2">Incentive 1 (₹)</th><th className="text-left px-2">Incentive 2 (₹)</th><th className="text-left px-2">Incentive 3 (₹)</th>
                  <th className="text-left px-2">Earned</th><th className="px-2" />
                </tr>
              </thead>
              <tbody className={loading ? "opacity-60" : ""}>
                {(byUser?.items || []).map((u) => (
                  <tr key={u.user_id} className="border-t border-neutral-100">
                    <td className="py-2 px-3 font-medium">{u.name}</td>
                    <td className="px-2 text-neutral-500">{u.role_code}</td><td className="px-2 text-neutral-500">{u.zone}</td>
                    <td className="px-2 text-neutral-500 text-xs">{u.scope === "area" ? "Zone" : "Mapped"} · {u.distributors_with_target}/{u.distributors} distributors with target</td>
                    <td className="px-2 text-right font-mono">{kg(u.t1_kg)}</td><td className="px-2 text-right font-mono">{kg(u.t2_kg)}</td><td className="px-2 text-right font-mono">{kg(u.t3_kg)}</td>
                    <td className="px-2 text-right font-mono">{kg(u.achieved_kg)}</td><td className="px-2 text-right font-mono">{kg(u.primary_kg)}</td>
                    <td className="px-2"><Pcts progress={u.progress} /></td>
                    {["i1", "i2", "i3"].map((k) => (
                      <td key={k} className="px-2">
                        <Input type="number" min="0" step="1" value={incCur(u)[k]} onChange={(ev) => setInc(u, k, ev.target.value)}
                               className="h-8 w-[100px]" data-testid={`dt-inc-${k}-${u.user_id}`} />
                      </td>
                    ))}
                    <td className="px-2 text-xs" data-testid={`dt-earned-${u.user_id}`}>
                      {u.earned_level ? <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">₹{Number(u.earned_amount).toLocaleString("en-IN")} · T{u.earned_level}</span>
                        : <span className="text-neutral-400">—</span>}
                    </td>
                    <td className="px-2">
                      {incDirty(u) && (
                        <Button size="sm" className="h-8" disabled={saving === u.user_id} onClick={() => saveInc(u)} data-testid={`dt-inc-save-${u.user_id}`}>
                          {saving === u.user_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />} Save
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {!loading && (byUser?.items || []).length === 0 && <tr><td colSpan={14} className="py-10 text-center text-neutral-400">No users.</td></tr>}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>}

      {tab !== "shares" && (() => {
        const d = tab === "distributors" ? data : byUser;
        if (!d) return null;
        return (
          <div className="flex items-center justify-between text-xs text-neutral-500">
            <span>{d.total ? `Showing ${(page - 1) * PAGE + 1}–${(page - 1) * PAGE + d.items.length} of ${d.total}` : "No rows"}</span>
            <span className="flex items-center gap-2">
              {tab === "distributors" && dirtyRows.length > 0 && (
                <Button size="sm" onClick={saveAll} className="tulsi-gradient text-white" data-testid="dt-save-all">
                  <Save className="h-4 w-4 mr-1" /> Save {dirtyRows.length} change{dirtyRows.length === 1 ? "" : "s"}
                </Button>
              )}
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="dt-prev">Prev</Button>
              <Button variant="outline" size="sm" disabled={page * PAGE >= d.total} onClick={() => setPage((p) => p + 1)} data-testid="dt-next">Next</Button>
            </span>
          </div>
        );
      })()}
      <p className="text-[11px] text-neutral-400 flex items-center gap-1"><Target className="h-3 w-3" />
        Leave Target 1 empty and press Save to remove a distributor's target. Target 2 needs Target 1, Target 3 needs Target 2, each at least the one before.
        {tab === "users" && " Incentive is earned when BOTH secondary and primary kg reach a target level; the highest level reached is paid."}</p>
    </div>
  );
}
